import React, { memo, useCallback, useEffect, useState } from 'react'

import { Button, Check, Close, Icon } from '@cgtrader/triangle-ui/dist/src/ssr-index'

import { iconSizeMedium } from 'Pods/ThreeDStudio/constants'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import {
  hasSeenNotificationsPopover,
  markNotificationsPopoverSeen,
} from 'Pods/ThreeDStudio/Services/notificationPreferencesStorage'
import useNotificationPreferences from 'Pods/ThreeDStudio/Shared/NotificationPreferences/useNotificationPreferences'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'

import useFocusTrap from 'Utils/useFocusTrap'

import ToggleRow from './Components/ToggleRow/ToggleRow'

const TITLE_ID = 'notifications-popover-title'

function NotificationsPopover() {
  const t = useTranslation()
  const isGenerating = useThreeDStudioStore(state => state.generation?.isGenerating)
  const {
    preferences,
    isBrowserPushSupported,
    isEmailPreferenceAvailable,
    setEmailWhenReady,
    setBrowserNotifications,
  } = useNotificationPreferences()
  const [isOpen, setIsOpen] = useState(false)
  const { trapRef } = useFocusTrap(isOpen)

  const handleDismiss = useCallback(() => {
    markNotificationsPopoverSeen()
    setIsOpen(false)
  }, [])

  useEffect(() => {
    if (isGenerating && !hasSeenNotificationsPopover()) {
      setIsOpen(true)
    }
  }, [isGenerating])

  useEffect(() => {
    if (!isOpen) return undefined

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') handleDismiss()
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, handleDismiss])

  if (!isOpen) return null

  return (
    <>
      {/* The scrim dims the page so the notice cannot be missed. It also blocks the
          page, which is why the notice below is a real modal dialog rather than the
          passive region it used to be: focus moves into it and stays there while it
          is open, and Escape or a click on the scrim dismisses it. */}
      <div className="notifications-popover__scrim" onClick={handleDismiss} aria-hidden="true" />
      <div
        ref={trapRef}
        className="notifications-popover"
        role="dialog"
        aria-modal="true"
        aria-labelledby={TITLE_ID}
      >
        <div className="notifications-popover__header">
          <h2 id={TITLE_ID} className="notifications-popover__title cgt-heading-50">
            {t('notificationsPopover.title')}
          </h2>
          <button
            type="button"
            className="notifications-popover__close"
            aria-label={t('notificationsPopover.close')}
            onClick={handleDismiss}
          >
            <Icon svgComponent={Close} color="darker" size={iconSizeMedium} notSpaced />
          </button>
        </div>
        <div className="notifications-popover__preferences">
          <div className="notifications-popover__indicator">
            <Icon svgComponent={Check} color="primary" size={iconSizeMedium} notSpaced />
            <p className="notifications-popover__indicator-text cgt-body-base">
              {t('notificationsPopover.indicator.lead')}
              {' '}
              <span className="cgt-body-base-semibold">{t('notificationsPopover.indicator.highlight')}</span>
              {' '}
              {t('notificationsPopover.indicator.rest')}
            </p>
          </div>
          {isEmailPreferenceAvailable && (
            <ToggleRow
              id="notifications-popover-email-toggle"
              label={t('notificationsPopover.emailToggle')}
              isChecked={preferences.emailWhenReady}
              onChange={setEmailWhenReady}
            />
          )}
          {isBrowserPushSupported && (
            <ToggleRow
              id="notifications-popover-browser-toggle"
              label={t('notificationsPopover.browserToggle')}
              isChecked={preferences.browserNotifications}
              onChange={setBrowserNotifications}
            />
          )}
        </div>
        <p className="notifications-popover__note cgt-body-base">
          {t('notificationsPopover.note')}
        </p>
        <Button
          actionType="button"
          size="small"
          customClass="notifications-popover__save"
          onClick={handleDismiss}
        >
          {t('notificationsPopover.save')}
        </Button>
      </div>
    </>
  )
}

export default memo(NotificationsPopover)
